<template>
  <div class="page-container">
    <div class="header">
      <div class="title">分账记录</div>
    </div>
    <div class="search-form">
      <el-form
        class="search-form-content"
        inline
        :model="form"
        label-width="160px"
        label-position="left">
        <el-row>
          <el-col :span="6">
            <el-form-item label="接收方商户编号/名称" size="small">
              <el-input v-model="form.receiveMerchantIdOrName" placeholder="请输入接收方商户编号/名称" clearable/>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="分账批次号" size="small">
              <el-input v-model="form.splitBatchNo" placeholder="请输入分账批次号" clearable/>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="分账流水单号" size="small">
              <el-input v-model="form.splitSerialNo" placeholder="请输入分账流水单号" clearable/>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="分账状态" size="small">
              <el-select v-model="form.statuses">
                <el-option :value="0" label="处理中"/>
                <el-option :value="1" label="分账成功"/>
                <el-option :value="2" label="分账失败"/>
                <el-option :value="3" label="分账取消"/>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="10">
            <el-form-item label="分账申请时间" size="small">
              <el-date-picker
                v-model="dateTimeRange"
                :picker-options="pickerOptions"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                align="right"
                size="small"/>
            </el-form-item>
          </el-col>
          <el-col :span="10">
            <el-form-item label="分账完成时间" size="small">
              <el-date-picker
                v-model="finishTimeRange"
                :picker-options="pickerOptions"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                align="right"
                size="small"/>
            </el-form-item>
          </el-col>
          <el-col :span="4">
            <el-form-item size="small">
              <el-button type="primary" @click="handleFormSearch" class="bs-btn" :loading="table.loading">查询</el-button>
              <el-button @click="handleFormReset" class="bs-btn2">重置</el-button>
              <el-button type="primary" @click="exportTableFunc" class="bs-btn" :loading="exportLoading">导出</el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>
    <div class="table-container">
      <el-table :data="table.data" style="width: 100%;" border v-loading="table.loading">
        <el-table-column prop="splitBatchNo" label="分账批次号" align="center" min-width="250px"/>
        <el-table-column prop="splitSerialNo" label="分账流水单号" align="center" min-width="250px"/>
        <el-table-column prop="receiveMerchantId" label="接收方商户编号" align="center" min-width="150px"/>
        <el-table-column prop="receiveMerchantName" label="接收方商户名称" align="center" min-width="150px"/>
        <el-table-column prop="splitAmount" label="分账金额" align="center" min-width="150px">
          <template slot-scope="{row: {splitAmount}}">{{ divide(splitAmount, 100) }}</template>
        </el-table-column>
        <el-table-column prop="realAmount" label="实际到账金额" align="center" min-width="150px">
          <template slot-scope="{row: {realAmount}}">{{ divide(realAmount, 100) }}</template>
        </el-table-column>
        <el-table-column prop="splitStatus" label="分账状态" align="center" min-width="150px">
          <template slot-scope="{row: {splitStatus}}">{{ splitStatusOptions[splitStatus] || '' }}</template>
        </el-table-column>
        <el-table-column prop="failReason" label="失败原因" align="center" min-width="150px"/>
        <el-table-column prop="splitDate" label="分账申请时间" align="center" min-width="200px"/>
        <el-table-column prop="creditedDate" label="完成时间" align="center" min-width="200px"/>
        <el-table-column label="操作" align="center" fixed="right" min-width="200px">
          <template slot-scope="{row}">
            <el-button type="text" @click="handleTableDownload(row)">下载分账批次文件</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-content">
        <el-pagination
          v-if="table.totalCount > 0"
          :page-size="table.pageSize"
          layout="total, prev, pager, next, jumper"
          :total="table.totalCount"
          :current-page="table.pageNo"
          @current-change="onChangeCurrentPage"/>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { exportPageSplitSerial, exportSaDetailByBatch, queryPageSplitSerial } from '../../../api/cloud-manage';
import { debounce } from "@/utils/index";
import { divide, multiply, subtract } from '../../../utils/math';

const FORMAT = 'yyyy-MM-DD HH:mm:ss';
const start = moment().subtract(90, 'days').startOf('d').format(FORMAT);
const end = moment().endOf('d').format(FORMAT);

export default {
  name: "divide-accounts",
  data() {
    return {
      splitStatusOptions: {
        0: '处理中',
        1: '分账成功',
        2: '分账失败',
        3: '分账取消',
      },
      dateTimeRange: [start, end],
      finishTimeRange: [],
      pickerOptions: {
        disabledDate(time) {
          // 日期只能选最近三个月的时间
          const threeMonthAgo = moment().subtract(90, 'days').valueOf();
          return moment(time).valueOf() < threeMonthAgo;
        },
      },
      form: {
        receiveMerchantIdOrName: '',
        splitBatchNo: '',
        splitSerialNo: '',
        statuses: '',
      },
      exportLoading: false,
      table: {
        loading: false,
        data: [],
        pageNo: 1,
        totalCount: 0,
        pageSize: 10,
      },
    };
  },
  computed: {
    exportTable() {
      return debounce(this.exportTableFunc, 500, true);
    },
  },
  mounted() {
    if (this.$route.query.splitStatus !== undefined) {
      this.form.statuses = parseInt(this.$route.query.splitStatus, 10);
    }
    this.handleFormSearch();
  },
  methods: {
    divide,
    handleFormSearch() {
      this.table.pageNo = 1;
      this.queryTableData();
    },
    handleFormReset() {
      this.form = {
        receiveMerchantIdOrName: '',
        splitBatchNo: '',
        splitSerialNo: '',
        statuses: '',
      };
      this.dateTimeRange = [start, end];
      this.finishTimeRange = [start, end];
      this.handleFormSearch();
    },
    async queryTableData() {
      const { pageNo, pageSize } = this.table;
      const [startTime, endTime] = this.dateTimeRange || [];
      const [finishStartTime, finishEndTime] = this.finishTimeRange || [];
      const params = {
        ...this.form,
        pageNo,
        pageSize,
        startTime,
        endTime,
        finishStartTime,
        finishEndTime,
      };
      this.table.loading = true;
      const { data: { data: { list, totalCount } } } = await queryPageSplitSerial(params).finally(() => {
        this.table.loading = false;
      });
      this.table.data = list;
      this.table.totalCount = totalCount;
    },
    exportTableFunc() {
      const [startTime, endTime] = this.dateTimeRange || [];
      const params = {
        ...this.form,
        startTime,
        endTime,
      };
      this.exportLoading = true;
      exportPageSplitSerial(params).then(({ data: { code } }) => {
        if (code === '0') {
          this.$message.success('导出成功，请到下载中心下载报表文件！');
        }
      }).finally(() => {
        this.exportLoading = false;
      });
    },
    handleTableDownload({ splitBatchNo }) {
      exportSaDetailByBatch({ splitBatchNo }).then(({ data: { code } }) => {
        if (code === '0') {
          this.$message.success('导出成功，请到下载中心下载报表文件！');
        }
      });
    },
    /**
     * 改变当前页事件
     * @param  {number} pageNo 当前页码
     */
    onChangeCurrentPage(pageNo) {
      this.table.pageNo = pageNo;
      this.queryTableData();
    },
  },
};
</script>

<style scoped lang="scss">
.page-container {
  > .header {
    padding-left: 50px;
    background-color: #fff;

    .title {
      position: relative;
      height: 80px;
      font-size: 18px;
      line-height: 80px;
      color: #333;

      &::after {
        position: absolute;
        right: 0;
        bottom: 0;
        left: -50px;
        display: block;
        height: 0;
        content: "";
        border-bottom: 1px solid #f2f3f3;
      }
    }
  }

  .search-form {
    padding: 40px 50px 20px;
    border-bottom: 20px solid #eee;

    .search-form-content {
      /deep/ .el-select,
      /deep/ .el-input {
        width: 210px;
      }
    }
  }

  .table-container {
    padding: 40px 50px 0;
    background: #fff;

    .pagination-content {
      margin-top: 10px;
      text-align: center;
    }
  }
}
</style>
